/**
 * nimbus-cart-drawer.js
 * Vereist cart.js (CartItems, CartRemoveButton).
 */

/* CartDrawer ─────────────────────────────────────────────── */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this._wireCartIcon();
  }

  _wireCartIcon() {
    var link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click',   function(e) { e.preventDefault(); this.open(link); }.bind(this));
    link.addEventListener('keydown', function(e) { if (e.code.toUpperCase() === 'SPACE') { e.preventDefault(); this.open(link); } }.bind(this));
  }

  open(trigger) {
    if (trigger) this.activeElement = trigger;
    /* always pull the current cart before/while showing the drawer */
    this.refresh();
    document.body.classList.add('overflow-hidden', 'nimbus-cart-open');
    void this.querySelector('.nimbus-cart__panel')?.offsetWidth;
    requestAnimationFrame(function() { this.classList.add('is-open'); }.bind(this));
    setTimeout(function() { this.querySelector('.nimbus-cart__close')?.focus(); }.bind(this), 500);
  }

  /* Fetch the live cart + re-render drawer contents and the count badge.
     Bulletproof: works no matter how items were added (form, quick-add, bundle). */
  refresh() {
    var self = this;
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + '?sections=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newInner = doc.querySelector('.nimbus-cart__inner');
          var curInner = self.querySelector('.nimbus-cart__inner');
          if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;
          var newDrawer = doc.querySelector('cart-drawer');
          if (newDrawer) self.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        }
        return fetch('/cart.js', { credentials: 'same-origin' });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
        if (window.NimbusCurrency && window.NimbusCurrency.refresh) window.NimbusCurrency.refresh();
        document.dispatchEvent(new CustomEvent('nimbus:cart:updated', { detail: { count: cart.item_count } }));
      })
      .catch(function () {});
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden', 'nimbus-cart-open');
    var el = this.activeElement;
    setTimeout(function() { if (el) el.focus(); }, 50);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    var self = this;
    this.getSectionsToRender().forEach(function(s) {
      var el = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
      if (!el) return;
      var html = parsedState.sections[s.id];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var target = parsed.querySelector(s.selector || '.shopify-section');
      if (target) el.innerHTML = target.innerHTML;
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    var self2 = this;
    setTimeout(function() { self2.open(); });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer',    selector: '.nimbus-cart__inner' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector) {
    selector = selector || '.shopify-section';
    if (!html) return '';
    var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }
  getSectionDOM(html, sel) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(sel || '.shopify-section');
  }
  setActiveElement(el) { this.activeElement = el; }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);


/* CartDrawerItems ─────────────────────────────────────────── */
class CartDrawerItems extends CartItems {

  getSectionsToRender() {
    return [
      { id: 'CartDrawer',  section: 'cart-drawer',    selector: '.nimbus-cart__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return '';
    var el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  resetQuantityInput(id) {
    var input = this.querySelector('#Drawer-quantity-' + id);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  updateLiveRegions(line, message) {
    try {
      var lineErr = document.getElementById('CartDrawer-LineItemError-' + line);
      if (lineErr) {
        var txt = lineErr.querySelector('.cart-item__error-text');
        if (txt) txt.textContent = message || '';
      }
      var region = document.getElementById('CartDrawer-LiveRegionText');
      if (region) {
        region.setAttribute('aria-hidden', 'false');
        setTimeout(function() { region.setAttribute('aria-hidden', 'true'); }, 1000);
      }
    } catch(e) {}
  }

  /* Volledig overschreven — verwijdert crashende trapFocus calls + .drawer__inner-empty */
  updateQuantity(line, quantity, event, name, variantId) {
    var self = this;
    var sections = this.getSectionsToRender();

    this.enableLoading(line);

    var body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: sections.map(function(s) { return s.section; }),
      sections_url: window.location.pathname
    });

    fetch(routes.cart_change_url, Object.assign({}, fetchConfig(), { body: body }))
      .then(function(r) { return r.text(); })
      .then(function(state) {
        var parsedState = JSON.parse(state);

        /* Toon Shopify-fouten (bijv. max qty bereikt) */
        if (parsedState.errors) {
          var qEl = document.getElementById('Drawer-quantity-' + line);
          if (qEl) qEl.value = qEl.getAttribute('value');
          self.updateLiveRegions(line, parsedState.errors);
          return;
        }

        /* Leeg-state op cart-drawer togglen */
        var drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', parsedState.item_count === 0);
        self.classList.toggle('is-empty', parsedState.item_count === 0);

        /* Nimbus header count badge bijwerken (wordt niet door de sectie-re-render gedekt) */
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = parsedState.item_count;
          el.classList.toggle('is-empty', parsedState.item_count === 0);
        });
        if (window.NimbusCurrency && window.NimbusCurrency.refresh) window.NimbusCurrency.refresh();

        /* Sla huidige fill-breedte op vóór re-render (voor smooth animatie) */
        var oldFill = document.querySelector('.nimbus-cart__shipping-fill');
        var oldPct  = oldFill ? (parseFloat(oldFill.style.width) || 0) : 0;

        /* Sectie DOM update */
        sections.forEach(function(section) {
          var wrapper = document.getElementById(section.id);
          if (!wrapper) return;
          var el = wrapper.querySelector(section.selector) || wrapper;
          var html = parsedState.sections[section.section];
          if (!html) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });

        /* Animeer shipping fill van oud naar nieuw:
           1. Zet fill op oude breedte (zonder transitie)
           2. Force reflow
           3. Herstel transitie → CSS animeert smooth naar Liquid-berekende breedte */
        var newFill = document.querySelector('.nimbus-cart__shipping-fill');
        if (newFill) {
          var targetPct = parseFloat(newFill.style.width) || 0;
          newFill.style.transition = 'none';
          newFill.style.width = oldPct + '%';
          void newFill.offsetWidth; /* force reflow */
          newFill.style.transition  = '';
          newFill.style.width = targetPct + '%';
        }

        /* Tekst + badge van shipping bar updaten */
        self.updateShippingBar(parsedState.total_price);

        /* Live regio & fout-tekst */
        self.updateLiveRegions(line, '');

        /* PubSub event (cart-icon-bubble teller) */
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        }
      })
      .catch(function() {
        var errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings && window.cartStrings.error ? window.cartStrings.error : '';
      })
      .finally(function() {
        self.disableLoading(line);
        /* Pulse stoppen op subtotaal */
        var sv = document.querySelector('.nimbus-cart__subtotal-value');
        if (sv) sv.classList.remove('is-updating');
      });
  }

  updateShippingBar(totalCents) {
    var threshold = 4995;
    var pct       = Math.min(100, Math.round(totalCents * 100 / threshold));
    var remaining = Math.max(0, threshold - totalCents);

    var fill = document.querySelector('.nimbus-cart__shipping-fill');
    if (fill) fill.style.width = pct + '%';

    var badge = document.querySelector('.nimbus-cart__shipping-badge');
    if (badge) badge.classList.toggle('nimbus-cart__shipping-badge--done', pct >= 100);

    var msg = document.querySelector('.nimbus-cart__shipping-msg');
    if (msg) {
      if (pct >= 100) {
        msg.innerHTML = "You've unlocked <strong>FREE shipping!</strong>";
      } else {
        var euros = (remaining / 100).toFixed(2).replace('.', ',');
        msg.innerHTML = "You're <strong>\u20AC" + euros + "</strong> away from FREE shipping!";
      }
    }

    var track = document.querySelector('.nimbus-cart__shipping-track');
    if (track) track.setAttribute('aria-valuenow', String(pct));
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);


/* +/- knoppen — met directe visuele feedback ─────────────── */
/* Refresh the cart drawer whenever anything reports a cart change. */
document.addEventListener('cart:refresh', function () {
  var d = document.querySelector('cart-drawer');
  if (d && typeof d.refresh === 'function') d.refresh();
});

document.addEventListener('click', function(e) {
  var btn = e.target.closest('.nimbus-cart__qty-btn');
  if (!btn || btn.disabled) return;

  var wrap  = btn.closest('.nimbus-cart__qty-wrap');
  var input = wrap && wrap.querySelector('.nimbus-cart__qty-number');
  if (!input) return;

  var current = parseInt(input.value)  || 1;
  var min     = parseInt(input.min)    || 1;
  var max     = input.max ? parseInt(input.max) : Infinity;
  var step    = parseInt(input.step)   || 1;

  var next = btn.name === 'minus'
    ? Math.max(min, current - step)
    : (max === Infinity ? current + step : Math.min(max, current + step));

  if (next === current) return;

  input.value = next;

  /* Disable/enable knoppen direct */
  var minus = wrap.querySelector('[name="minus"]');
  if (minus) {
    minus.disabled = next <= min;
    minus.setAttribute('aria-disabled', next <= min ? 'true' : 'false');
  }
  var plus = wrap.querySelector('[name="plus"]');
  if (plus && max !== Infinity) plus.disabled = next >= max;

  /* ── Directe visuele feedback ── */
  var itemEl = btn.closest('.nimbus-cart__item');
  if (itemEl) {
    /* Dim het item zodat gebruiker weet dat het verwerkt wordt */
    itemEl.classList.add('is-loading');

    /* Optimistische prijs: bereken nieuwe regelprijs direct */
    var unitPrice = parseInt(itemEl.dataset.unitPrice) || 0;
    if (unitPrice > 0) {
      var newLineTotal = unitPrice * next;
      var priceEl = itemEl.querySelector('.nimbus-cart__item-price-final');
      if (priceEl) {
        var euros = (newLineTotal / 100).toFixed(2).replace('.', ',');
        priceEl.textContent = '€' + euros;
      }
    }
  }

  /* Subtotaal pulseren terwijl AJAX loopt */
  var subtotalVal = document.querySelector('.nimbus-cart__subtotal-value');
  if (subtotalVal) subtotalVal.classList.add('is-updating');

  input.dispatchEvent(new Event('change', { bubbles: true }));
});


/* ─────────────────────────────────────────────────────────
   Discount code handler
   Gebruikt Shopify /discount/CODE endpoint + sectie re-render
   ───────────────────────────────────────────────────────── */
(function() {
  function initDiscount() {
    var input    = document.getElementById('nimbus-discount-input');
    var applyBtn = document.getElementById('nimbus-discount-apply');
    var feedback = document.getElementById('nimbus-discount-feedback');
    var applied  = document.getElementById('nimbus-discount-applied');

    if (!input || !applyBtn) return;

    /* Enter key op input */
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); }
    });

    applyBtn.addEventListener('click', applyDiscount);

    /* Remove-knoppen (voor server-rendered codes) */
    if (applied) {
      applied.addEventListener('click', function(e) {
        var btn = e.target.closest('.nimbus-cart__discount-remove');
        if (btn) removeDiscount(btn.dataset.code);
      });
    }

    function setFeedback(msg, type) {
      feedback.textContent = msg;
      feedback.className = 'nimbus-cart__discount-feedback is-visible nimbus-cart__discount-feedback--' + type;
      if (type === 'success') setTimeout(function() { feedback.classList.remove('is-visible'); }, 3500);
    }

    function setLoading(loading) {
      applyBtn.disabled = loading;
      applyBtn.textContent = loading ? '...' : (window.cartStrings && window.cartStrings.apply ? window.cartStrings.apply : 'Apply');
    }

    function refreshCartSections(callback) {
      var cartDrawerItems = document.querySelector('cart-drawer-items');
      if (!cartDrawerItems) { if (callback) callback(); return; }

      var sections = ['cart-drawer', 'cart-icon-bubble'];
      var params = new URLSearchParams();
      sections.forEach(function(s) { params.append('sections[]', s); });

      fetch('/cart?sections=' + sections.join(',') + '&sections_url=' + encodeURIComponent(window.location.pathname), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        /* Update de cart drawer inner */
        var cartDrawer = document.getElementById('CartDrawer');
        if (cartDrawer && data['cart-drawer']) {
          var parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          var inner  = parsed.querySelector('.nimbus-cart__inner');
          var currentInner = cartDrawer.querySelector('.nimbus-cart__inner');
          if (inner && currentInner) currentInner.innerHTML = inner.innerHTML;
        }
        /* Update cart icon bubble */
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data['cart-icon-bubble']) {
          var parsed2 = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          var fresh   = parsed2.querySelector('.shopify-section');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }
        if (callback) callback();
      })
      .catch(function() { if (callback) callback(); });
    }

    function applyDiscount() {
      var code = input.value.trim();
      if (!code) { setFeedback('Voer een kortingscode in.', 'error'); return; }

      setLoading(true);
      feedback.classList.remove('is-visible');

      /* Stap 1: pas de code toe via Shopify discount endpoint */
      fetch('/discount/' + encodeURIComponent(code), {
        credentials: 'same-origin',
        redirect: 'follow',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(function() {
        /* Stap 2: controleer of de code geldig is via /cart.js */
        return fetch('/cart.js', { credentials: 'same-origin' });
      })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var codes = cart.discount_codes || [];
        var found = codes.find(function(d) {
          return d.code.toLowerCase() === code.toLowerCase();
        });

        if (found && found.applicable) {
          input.value = '';
          setFeedback('\u2713 "' + found.code + '" is toegepast!', 'success');
          refreshCartSections(function() {
            /* Herbind discount remove knoppen na re-render */
            var newApplied = document.getElementById('nimbus-discount-applied');
            if (newApplied) {
              newApplied.addEventListener('click', function(e) {
                var btn = e.target.closest('.nimbus-cart__discount-remove');
                if (btn) removeDiscount(btn.dataset.code);
              });
            }
          });
        } else {
          setFeedback('Ongeldige of verlopen kortingscode.', 'error');
        }
        setLoading(false);
      })
      .catch(function() {
        setFeedback('Er is iets misgegaan. Probeer het opnieuw.', 'error');
        setLoading(false);
      });
    }

    function removeDiscount(code) {
      /* Verwijder door een lege code in te stellen */
      fetch('/discount/', {
        credentials: 'same-origin',
        redirect: 'follow',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(function() {
        return fetch('/cart.js', { credentials: 'same-origin' });
      })
      .then(function() {
        feedback.classList.remove('is-visible');
        refreshCartSections();
      })
      .catch(function() {});
    }
  }

  /* Init na DOM ready (en opnieuw na sectie re-render) */
  document.addEventListener('DOMContentLoaded', initDiscount);
  document.addEventListener('nimbus:cart:updated', initDiscount);
})();
